// Stream-health presentation — the one calm vocabulary every surface uses to be
// HONEST about the live connection (Brief 5 / pt10 + pt20).
//
// The problem this closes: the SSE reader self-reconnects silently, so when the
// feed is dead the screen looks identical to "everything's calm" — minting false
// trust. The fix is to make the system honest about not knowing. This module is
// the pure presentation half: health (+ how stale the feed is) → a calm cue and
// whether the live region should be dimmed. No React, no DOM — unit-tested
// directly so the honesty rules can't silently regress.
//
// Design law (design_system.md): calm, never alarm. The reserved
// signal-attention amber is OFF-LIMITS here — stream-health is NEVER a "needs
// you" attention call; it uses the neutral signal-stopped token. `live` shows no
// chrome at all (quiet = calm-competence, carried by the ambient heartbeat).

import type { StreamHealth } from "@waytide/core";

const MINUTE_MS = 60_000;
const HOUR_MS = 60 * MINUTE_MS;

/** A short relative "how long ago" for the last live frame ("just now" / "3m" /
 * "2h"), or "" when there has been no frame yet (so the caller omits the slot
 * rather than print a misleading age). `now` is injectable for tests. */
export function lastUpdateAgo(
  lastEventAt: number | null,
  now: number = Date.now(),
): string {
  if (lastEventAt == null || !Number.isFinite(lastEventAt)) return "";
  const diff = now - lastEventAt;
  if (diff < MINUTE_MS) return "just now";
  if (diff < HOUR_MS) return `${Math.floor(diff / MINUTE_MS)}m`;
  return `${Math.floor(diff / HOUR_MS)}h`;
}

/** What a surface needs to render the health honestly. */
export interface StreamHealthCue {
  /** True only when the connection is live — the ONLY state a surface may paint
   * as calm (no chrome, the ambient heartbeat carries "alive"). */
  live: boolean;
  /** True whenever the live region should be visibly dimmed because the feed is
   * not trustworthy right now (anything but `live`). */
  dim: boolean;
  /** A calm one-line status, or "" when live (live shows no chrome). */
  label: string;
  /** The neutral tone key the surfaces map to CSS — NEVER the reserved amber.
   * `none` while live; `stopped` (the neutral grey) otherwise. */
  tone: "none" | "stopped";
}

/**
 * Reduce the live health (and the last-frame age) to the calm cue a header
 * shows. Honest by construction:
 *   - `live`         → no chrome, no dim (the heartbeat carries "alive").
 *   - `connecting`   → a quiet "connecting…" while we first attach.
 *   - `reconnecting` → a quiet "reconnecting…" — we KNOW the feed dropped.
 *   - `dead`         → "stream lost" + how stale ("last update 3m ago") so the
 *                      staleness is concrete; falls back to a bare "stream lost"
 *                      when no frame ever arrived.
 * `dim` is true for every non-live state so a stale stream looks stale.
 */
export function streamHealthCue(
  health: StreamHealth,
  lastEventAt: number | null,
  now: number = Date.now(),
): StreamHealthCue {
  if (health === "live") {
    return { live: true, dim: false, label: "", tone: "none" };
  }
  if (health === "connecting") {
    return { live: false, dim: true, label: "connecting…", tone: "stopped" };
  }
  if (health === "reconnecting") {
    return { live: false, dim: true, label: "reconnecting…", tone: "stopped" };
  }
  // dead
  const ago = lastUpdateAgo(lastEventAt, now);
  // "just now" reads naturally as-is; a duration ("5m"/"2h") takes "ago" so the
  // sentence reads "last update 5m ago". No prior frame → a bare "stream lost".
  const phrase = ago === "" ? "" : ago === "just now" ? ago : `${ago} ago`;
  const label = phrase ? `stream lost — last update ${phrase}` : "stream lost";
  return { live: false, dim: true, label, tone: "stopped" };
}
